import React from 'react';
import moment from 'moment';
import { withRouter } from 'react-router-dom';
import ProgressSpinner from '@avtechsoftware/react-component-library/src/components/common/ProgressSpinner';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons';
import PropTypes from 'prop-types';
import OrderShippingProgress from './OrderShippingProgress/OrderShippingProgress';
import CrmShipment from '../../../classes/CrmShipment';
import LineItemTable from '../LineItemTable/LineItemTable';
import AddressBlock from '../AddressBlock/AddressBlock';
import envConfig from '../../../envConfig';
import OrderStatusDisplayType from '../OrderStatusDisplayType';
import OrderShipmentList from './OrderShipmentList/OrderShipmentList';
import AddressType from '../../quotes/QuoteDetails/AddressType';
import OrderPaymentForm from '../OrderPaymentForm/OrderPaymentForm';
import OrderPaymentDisplayType from '../OrderPaymentDisplayType';
import PaymentSuccessModal from '../PaymentSuccessModal/PaymentSuccessModal';
import ShipmentTrackingModal from './ShipmentTrackingModal/ShipmentTrackingModal';
import convertNumberToFinancialString from '../../../utility/convertNumberToFinancialString';
import CrmOrderDetails from '../../../classes/CrmOrderDetails';
import ShareDropdown from '../../orders_quotes_shared/ShareDropdown/ShareDropdown';
import LinkModal from '../../common/LinkModal/LinkModal';
import GrantAccessModal from '../../orders_quotes_shared/GrantAccessModal/GrantAccessModal';
import BillingShareButton from '../../orders_quotes_shared/BillingShareButton/BillingShareButton';

class OrderDetails extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      paymentSuccess: false,
      showSuccessModal: false,
      submitting: false,
      shipmentList: [],
      loading: true,
      formErrors: [],
      cardNumber: '',
      expirationYear: moment().year(),
      expirationMonth: '01',
      ccv: '',
      showTrackingModal: false,
      trackShipmentId: 0,
      shipmentListError: false,
      showLinkModal: false,
      showGrantAccessModal: false,
      inputEmail: '',
    };

    this.getData = this.getData.bind(this);
    this.submitPayment = this.submitPayment.bind(this);
    this.backToList = this.backToList.bind(this);
    this.closeShipmentError = this.closeShipmentError.bind(this);
  }

  componentDidMount() {
    const { setSubscriptionSwapUrl } = this.props;
    setSubscriptionSwapUrl('/s/orders/list');
    this.getData().then();
  }

  getData() {
    const { remoteService, order, validationId } = this.props;
    this.setState({ loading: true });
    return remoteService.get(
      `${envConfig.apiUrl}/api/v1/shipments/shipment-list/${order.id}-${validationId}`,
    ).then((shipmentResponse) => {
      const newShipmentList = shipmentResponse.shipmentList.map(
        (shipment) => new CrmShipment(
          shipment.id,
          shipment.shippingDate,
          shipment.carrier,
          shipment.shipMethod,
          shipment.trackingNumber,
          shipment.shipmentItemList,
          shipment.address,
          shipment.distributionOrigin,
        ),
      );
      this.setState({
        shipmentList: newShipmentList,
        loading: false,
      });
    }).catch(() => {
      this.setState({
        shipmentListError: true,
        loading: false,
      });
    });
  }

  submitPayment() {
    const {
      email, isPublicView, order,
    } = this.props;
    const { inputEmail } = this.state;
    const {
      cardNumber, expirationYear, expirationMonth, ccv,
    } = this.state;
    this.setState({ submitting: true });
    const formData = new FormData();
    formData.append('cardNumber', cardNumber);
    formData.append('cardExpirationYear', (expirationYear % 100).toString());
    formData.append('cardExpirationMonth', expirationMonth.toString());
    formData.append('cardCvv', ccv);
    formData.append('orderCrmId', order.id);
    formData.append('email', isPublicView ? inputEmail : email);
    formData.append('purchaseOrderNumber', order.poNumber);
    formData.append('paymentAmount', order.paymentBalanceDue);
    formData.append('balanceDue', order.paymentBalanceDue);
    axios
      .post(`${envConfig.raaUrl}/order/pay-amount-due`, formData)
      .then((response) => {
        if (response.data && response.data.success === false) {
          this.setState({ formErrors: response.data.data });
        } else {
          this.setState({ paymentSuccess: true, showSuccessModal: true });
        }
      })
      .catch(() => {
        this.setState({
          formErrors: [
            {
              field: 'Generic',
              message: 'Unexpected error processing payment.',
            },
          ],
        });
      })
      .finally(() => {
        this.setState({ submitting: false });
      });
  }

  /* istanbul ignore next */
  backToList() {
    const { history } = this.props;
    history.push('/s/orders/list');
  }

  /* istanbul ignore next */
  closeShipmentError() {
    this.setState({ shipmentListError: false });
  }

  renderPaymentStatus() {
    const { order } = this.props;
    const { paymentSuccess } = this.state;
    switch (order.paymentType) {
      case OrderPaymentDisplayType.CANCELLED:
        return <>Cancelled</>;
      case OrderPaymentDisplayType.CLOSED:
        return <>Closed</>;
      case OrderPaymentDisplayType.REFUNDED:
        return <>Refunded</>;
      case OrderPaymentDisplayType.PAID:
        return (
          <>
            <FontAwesomeIcon
              icon={faCheckCircle}
              className="mr-1 text-success"
            />
            {' '}
            Paid
          </>
        );
      case OrderPaymentDisplayType.OPEN:
        return paymentSuccess === true ? (
          <>
            <FontAwesomeIcon
              icon={faCheckCircle}
              className="mr-1 text-success"
            />
            {' '}
            Paid
          </>
        ) : (
          <> Open</>
        );
      default: return <></>;
    }
  }

  render() {
    const {
      order,
      userHasShareLinkPrivileges,
      userHasGrantAccessPrivileges,
      isPublicView,
      supportsRoles,
      samlEnabled,
      validationId,
    } = this.props;
    const {
      shipmentList,
      paymentSuccess,
      submitting,
      showSuccessModal,
      cardNumber,
      expirationYear,
      expirationMonth,
      ccv,
      loading,
      formErrors,
      trackShipmentId,
      showTrackingModal,
      shipmentListError,
      showLinkModal,
      showGrantAccessModal,
      inputEmail,
    } = this.state;
    // eslint-disable-next-line react/destructuring-assignment
    const numShippedItems = shipmentList.reduce(
      (acc, curr) => acc + curr.shipmentItemQuantity,
      0,
    );
    const remainingItems = order.numItems - numShippedItems;
    const previousPayments = order.grandTotal - order.paymentBalanceDue;
    const needsPayment = order.paymentBalanceDue > 0
      && order.paymentType !== OrderPaymentDisplayType.PAID
      && order.paymentType !== OrderPaymentDisplayType.CANCELLED
      && paymentSuccess !== true;
    const trackedShipment = shipmentList.find(
      (shipment) => shipment.id === trackShipmentId,
    );
    return (
      <div className="container-fluid mt-3">
        <div className="col-12">
          <div className="row">
            <div className="col">
              <h2>
                Order
                {' '}
                {order.orderNumber}
              </h2>
              {order.createdAt && (
                <span className="pl-1">
                  Ordered on
                  {' '}
                  {moment.unix(order.createdAt).format('MMM DD, YYYY')}
                </span>
              )}
            </div>
            {!isPublicView && (
              <div className="ml-auto col-auto">
                <button
                  type="button"
                  className="btn btn-secondary ml-auto"
                  onClick={this.backToList}
                >
                  Back to List
                </button>
              </div>
            )}
          </div>
          <hr className="mb-4" />
          {!loading ? (
            <>
              <div className="row mb-2">
                <div className="col">
                  <OrderShippingProgress
                    orderStatusType={order.getShippingStatus(numShippedItems)}
                  />
                </div>

                <div className="col-md-auto ml-auto text-right col-sm-12 mb-3">
                  {order.poNumber && (
                  <div>
                    P.O.
                    {' '}
                    {order.poNumber}
                  </div>
                  )}
                  <div className="text-right">{this.renderPaymentStatus()}</div>
                  {/* istanbul ignore next */}
                  {order.referrerCode && (
                  <div className="text-right">
                    {`Referral Code: ${order.referrerCode}`}
                  </div>
                  )}

                  {!isPublicView && (
                  <div className="mt-2">
                    {
                    userHasShareLinkPrivileges
                      ? (
                        <BillingShareButton
                          onGetLink={
                      /* istanbul ignore next */
                      () => this.setState({ showLinkModal: true })
                    }
                        />
                      )
                      : (
                        <ShareDropdown
                          onGetLink={
                        /* istanbul ignore next */
                        () => this.setState({ showLinkModal: true })
                      }
                          onGrantAccountAccess={
                        /* istanbul ignore next */
                        () => this.setState({ showGrantAccessModal: true })
                      }
                          downloadPdfHref=""
                          showGrantAccessOption={userHasGrantAccessPrivileges}
                        />
                      )
                   }
                  </div>
                  )}
                </div>
              </div>

              <div className="row mb-3">
                <div className="col">
                  <OrderShipmentList
                    shipmentList={shipmentList}
                    remainingItems={remainingItems}
                    showRemainingItems={
                      remainingItems > 0
                      && order.getShippingStatus(numShippedItems)
                        !== OrderStatusDisplayType.COMPLETED
                    }
                    onTrackClick={(id) => this.setState({
                      trackShipmentId: id,
                      showTrackingModal: true,
                    })}
                    dataError={shipmentListError}
                    onCloseError={this.closeShipmentError}
                  />
                </div>
              </div>
              <div className="row pt-2">
                <div className="col">
                  <AddressBlock
                    addressMissing={order.shippingAddress.line1.length <= 0}
                    zipCode={order.shippingAddress.zipCode}
                    country={order.shippingAddress.country ? order.shippingAddress.country.name : ''}
                    city={order.shippingAddress.city}
                    contactName={order.shippingAddress.contactName}
                    addrLine2={order.shippingAddress.line2}
                    addrLine1={order.shippingAddress.line1}
                    state={order.shippingAddress.state}
                    title="Shipping Address"
                    type={AddressType.SHIPPING}
                  />
                </div>
                <div className="col">
                  <AddressBlock
                    addressMissing={order.billingAddress.line1.length <= 0}
                    zipCode={order.billingAddress.zipCode}
                    country={order.billingAddress.country ? order.billingAddress.country.name : ''}
                    city={order.billingAddress.city}
                    contactName={order.billingAddress.contactName}
                    addrLine2={order.billingAddress.line2}
                    addrLine1={order.billingAddress.line1}
                    state={order.billingAddress.state}
                    title="Billing Address"
                    type={AddressType.BILLING}
                  />
                </div>
              </div>
              <LineItemTable
                shippingCost={order.shippingCostDisplay}
                grandTotal={order.grandTotalDisplay}
                tax={order.taxDisplay}
                lineItems={order.lineItems}
                currencyType={order.currency}
                shippingDetailsString={order.shippingDetailsString}
              />

              <hr className="my-2" />

              {needsPayment && previousPayments !== 0 && (
                <>
                  {' '}
                  <div className="row">
                    <div className="col">
                      <h5>Previous Payments</h5>
                    </div>
                    <div className="col-auto ml-auto">
                      <h5>
                        {
                          convertNumberToFinancialString(
                            order.grandTotal - order.paymentBalanceDue,
                            order.currency,
                          )
                        }
                      </h5>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <h4>Balance Due</h4>
                    </div>
                    <div className="col-auto ml-auto">
                      <h4>
                        {order.paymentBalanceDueDisplay}
                      </h4>
                    </div>
                  </div>
                </>
              )}

              {needsPayment && (
                <div className="pb-5 pt-3">
                  <OrderPaymentForm
                    cardNumber={cardNumber}
                    setCardNumber={(cardNum) => {
                      this.setState({ cardNumber: cardNum });
                    }}
                    expirationYear={expirationYear}
                    setExpirationYear={(expYear) => {
                      this.setState({ expirationYear: expYear });
                    }}
                    expirationMonth={expirationMonth}
                    setExpirationMonth={(expMonth) => {
                      this.setState({ expirationMonth: expMonth });
                    }}
                    ccv={ccv}
                    setCcv={(newCcv) => {
                      this.setState({ ccv: newCcv });
                    }}
                    formErrors={formErrors}
                    isFormDisabled={submitting}
                    showErrorBanner
                    showEmail={isPublicView}
                    email={inputEmail}
                    setEmail={(newEmail) => {
                      this.setState({ inputEmail: newEmail });
                    }}
                  />
                  <div className="row mt-3">
                    <div className="col-sm-3" />
                    <div className="col-sm-9">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={this.submitPayment}
                      >
                        {submitting ? (
                          <>
                            <ProgressSpinner
                              progressText="Processing..."
                              useSmallSpinner
                            />
                          </>
                        ) : (
                          'Submit Payment'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="row d-flex justify-content-center mt-5">
              <div>
                <ProgressSpinner />
              </div>
            </div>
          )}
          {showSuccessModal && (
            <PaymentSuccessModal
              onClose={() => this.setState({ showSuccessModal: false })}
            />
          )}
          {showTrackingModal && (
            <ShipmentTrackingModal
              trackingNumber={trackedShipment.trackingNumber}
              shipTo={trackedShipment.shipToDisplay}
              shipFrom={trackedShipment.shipFromDisplay}
              onClose={() => this.setState({ showTrackingModal: false })}
            />
          )}
          {showLinkModal && (<LinkModal header="Get Link To This Order" url={`${envConfig.raaUrl}/p/order/ORD-${order.id}-${validationId}`} onClose={() => this.setState({ showLinkModal: false })} />)}
          {showGrantAccessModal && (
          <GrantAccessModal
            hasAdvancedRoles={supportsRoles}
            isSamlSubscription={samlEnabled}
            onClose={() => this.setState({ showGrantAccessModal: false })}
            isQuote={false}
            resourceId={order.id}
          />
          )}
        </div>
      </div>
    );
  }
}

export { OrderDetails };

OrderDetails.propTypes = {
  userHasGrantAccessPrivileges: PropTypes.bool.isRequired,
  userHasShareLinkPrivileges: PropTypes.bool.isRequired,
  supportsRoles: PropTypes.bool.isRequired,
  samlEnabled: PropTypes.bool.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  order: PropTypes.shape(CrmOrderDetails).isRequired,
  isPublicView: PropTypes.bool,
  setSubscriptionSwapUrl: PropTypes.func.isRequired,
  remoteService: PropTypes.shape({ get: PropTypes.func.isRequired }).isRequired,
  email: PropTypes.string,
  validationId: PropTypes.string.isRequired,
};

OrderDetails.defaultProps = {
  isPublicView: false,
  email: '',
};

export default withRouter(OrderDetails);
